import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdCarousel from "../../components/Client/AdCarousel";
import ClientHeader from "../../components/Client/ClientHeader";
import CategoryMegaMenu from "../../components/Client/CategoryMegaMenu";
import CategoryChips from "../../components/Client/CategoryChips";
import StoreShelf from "../../components/Client/StoreShelf";
import ProductShelf from "../../components/Client/ProductShelf";
import Footer from "../../components/Client/Footer";
import SupportButton from "../../components/Client/SupportButton";
import ScrollToTopButton from "../../components/Client/ScrollToTopButton";
import CustomServiceModal from "../../components/Client/CustomServiceModal";
import { getCategories } from "../../api/categoryService";
import { getProducts } from "../../api/productService";
import { getStores } from "../../api/storeService";
import { getHomeFeed } from "../../api/recommendationService";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "../../components/Client/clientTheme.css";
import "./MainPage.css";

export default function MainPage() {
  const { t } = useTranslation("mainPage");
  const { language } = useLanguage();
  const [categories, setCategories] = useState(null);
  const [stores, setStores] = useState(null);
  const [newArrivals, setNewArrivals] = useState(null);
  const [discounted, setDiscounted] = useState(null);
  const [bestSellers, setBestSellers] = useState(null);
  const [topRated, setTopRated] = useState(null);
  const [homeFeed, setHomeFeed] = useState(null);
  const [categoryShelves, setCategoryShelves] = useState({});
  const [categoryShelvesLoading, setCategoryShelvesLoading] = useState(true);
  const [customServiceOpen, setCustomServiceOpen] = useState(false);

  useEffect(() => {
    getCategories({ level: 1 }).then(setCategories).catch(() => setCategories([]));
    getStores({})
      .then((data) => setStores(data.slice(0, 10)))
      .catch(() => setStores([]));
    getProducts({ take: 12 }).then(setNewArrivals).catch(() => setNewArrivals([]));
    getProducts({ highlight: "discounted", take: 12 }).then(setDiscounted).catch(() => setDiscounted([]));
    getProducts({ highlight: "bestsellers", take: 12 }).then(setBestSellers).catch(() => setBestSellers([]));
    getProducts({ highlight: "toprated", take: 12 }).then(setTopRated).catch(() => setTopRated([]));
    getHomeFeed(12).then(setHomeFeed).catch(() => setHomeFeed({ fromWishlist: [] }));
  }, []);

  useEffect(() => {
    if (!categories || categories.length === 0) {
      setCategoryShelvesLoading(false);
      return;
    }
    setCategoryShelvesLoading(true);
    Promise.all(
      categories.map((category) =>
        getProducts({ categoryId: category.categoryId, take: 12 })
          .then((products) => [category.categoryId, products])
          .catch(() => [category.categoryId, []])
      )
    ).then((results) => {
      setCategoryShelves(Object.fromEntries(results));
      setCategoryShelvesLoading(false);
    });
  }, [categories]);

  return (
    <div className="client-main-page">
      <ClientHeader />
      <CategoryMegaMenu />

      <section className="client-hero">
        <div className="client-hero-inner">
          <div className="client-hero-text">
            <span className="client-hero-eyebrow">{t("welcomeTo")}</span>
            <h1 className="client-hero-title">{t("heroTitle")}</h1>
            <p className="client-hero-subtitle">
              {t("heroSubtitle")}
            </p>
            <button
              type="button"
              className="client-hero-custom-service-button"
              onClick={() => setCustomServiceOpen(true)}
            >
              {t("requestCustomService")}
            </button>
          </div>
          <AdCarousel />
        </div>
      </section>

      {customServiceOpen && <CustomServiceModal onClose={() => setCustomServiceOpen(false)} />}

      <CategoryChips title={t("categories")} categories={categories} loading={categories === null} />

      <StoreShelf title={t("popularStores")} stores={stores} loading={stores === null} viewAllHref="/stores" />

      <ProductShelf title={t("newArrivals")} products={newArrivals} loading={newArrivals === null} viewAllHref="/products" />
      <ProductShelf
        title={t("fromYourWishlist")}
        products={homeFeed?.fromWishlist}
        loading={homeFeed === null}
        viewAllHref="/wishlist"
      />

      {(categories || []).map((category) => (
        <ProductShelf
          key={category.categoryId}
          title={pickLocalizedName(language, category.name, category.nameArabic, category.nameHebrew)}
          products={categoryShelves[category.categoryId]}
          loading={categoryShelvesLoading && !categoryShelves[category.categoryId]}
          viewAllHref={`/products?categoryId=${category.categoryId}`}
        />
      ))}

      <ProductShelf
        title={t("discounted")}
        products={discounted}
        loading={discounted === null}
        viewAllHref="/products?highlight=discounted"
      />
      <ProductShelf
        title={t("bestSellers")}
        products={bestSellers}
        loading={bestSellers === null}
        viewAllHref="/products?highlight=bestsellers"
      />
      <ProductShelf
        title={t("topRated")}
        products={topRated}
        loading={topRated === null}
        viewAllHref="/products?highlight=toprated"
      />

      <Footer />
      <SupportButton />
      <ScrollToTopButton />
    </div>
  );
}
